import { useEffect, useMemo, useRef } from 'react';
import type { MutableRefObject } from 'react';
import type { UseFormTrigger } from 'react-hook-form';
import { makeCancellationRulesSchema } from '../schemas/cancellationRules.schema';
import type { CancellationRulesFormValues, CancellationRulesSchema } from '../schemas/cancellationRules.schema';
import type { CancellationFieldMeta } from '../services/cancellationRules';

export const EMPTY_CANCELLATION_SCHEMA = makeCancellationRulesSchema([]);

interface CancellationSchemaSyncOptions {
    formFieldMetas: CancellationFieldMeta[];
    schemaRef: MutableRefObject<CancellationRulesSchema>;
    isSubmitted: boolean;
    revalidate: UseFormTrigger<CancellationRulesFormValues>;
}

/** Keeps the resolver's schema in step with the live catalogue and clears bound errors that a
 * widened catalogue has made stale. Returns the narrow signature the panel's seed effect keys on. */
export const useCancellationSchemaSync = ({
    formFieldMetas,
    schemaRef,
    isSubmitted,
    revalidate,
}: CancellationSchemaSyncOptions): { fieldKeysSignature: string } => {
    // Narrow key+control signature so a background refetch that only widens a bound cannot re-fire
    // the panel's seed effect.
    const fieldKeysSignature = formFieldMetas.map((m) => `${m.key}:${m.control}`).join('|');

    // Wider signature (adds bounds/options) so a refetched bound rebuilds the Zod schema too.
    const schemaSignature = formFieldMetas
        .map((m) => `${m.key}:${m.control}:${m.min ?? ''}:${m.max ?? ''}:${(m.options ?? []).join(',')}`)
        .join('|');

    const schema = useMemo(() => makeCancellationRulesSchema(formFieldMetas), [schemaSignature]); // eslint-disable-line react-hooks/exhaustive-deps
    schemaRef.current = schema;

    const validatedSignature = useRef(schemaSignature);

    useEffect(() => {
        if (validatedSignature.current === schemaSignature) return;
        validatedSignature.current = schemaSignature;
        // Swapping the resolver's schema does not itself re-run validation, so an error rendered
        // under the old bounds would otherwise persist until the next edit or submit.
        if (isSubmitted) void revalidate();
    }, [schemaSignature, isSubmitted, revalidate]);

    return { fieldKeysSignature };
};
